import React from 'react';
import { Box, Card, CardContent, Chip, Typography, IconButton, CircularProgress, CircularProgressProps, Divider, Button } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import { useLocation, useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ISurvey } from '../../../classes/Survey';

type DimensionCode = 'A' | 'B' | 'C' | 'D' | 'E';
type InterpretationLevel = 'Dominante' | 'Alto' | 'Moderado' | 'Recesivo';

interface IDimensionInfo {
    name: string;
    color: 'primary' | 'secondary' | 'success' | 'warning' | 'error';
}

interface IInterpretation {
    level: InterpretationLevel;
    color: 'error' | 'warning' | 'info' | 'success';
}

type IResultTexts = Record<InterpretationLevel, { behavior: string; fit: string; }>;

interface IResponse {
    nativeQuestionId: number;
    content: string;
}

interface LocationState {
    survey: any;
    responses: any[];
}

const questionToDimensionMap: { [key: number]: DimensionCode } = {
    1: 'A', 2: 'E', 3: 'D', 4: 'B', 5: 'C',
    6: 'D', 7: 'C', 8: 'A', 9: 'B', 10: 'E',
    11: 'A', 12: 'D', 13: 'B', 14: 'E', 15: 'C',
    16: 'A', 17: 'C', 18: 'D', 19: 'E', 20: 'B',
    21: 'A', 22: 'D', 23: 'E', 24: 'B', 25: 'C',
    26: 'E', 27: 'A', 28: 'D', 29: 'C', 30: 'B',
    31: 'A', 32: 'E', 33: 'D', 34: 'C', 35: 'B',
    36: 'A', 37: 'D', 38: 'B', 39: 'E', 40: 'C',
    41: 'A', 42: 'C', 43: 'D', 44: 'E', 45: 'B',
    46: 'D', 47: 'E', 48: 'A', 49: 'C', 50: 'B',
};

const dimensionInfo: Record<DimensionCode, IDimensionInfo> = {
    A: { name: 'Orientación al Resultado', color: 'primary' },
    B: { name: 'Colaboración / Equipo', color: 'success' },
    C: { name: 'Innovación / Creatividad', color: 'secondary' },
    D: { name: 'Estabilidad / Certeza', color: 'warning' },
    E: { name: 'Desarrollo / Aprendizaje', color: 'error' },
};

const resultTexts: Record<DimensionCode, IResultTexts> = {
    A: {
        Dominante: { behavior: "La ejecución y la consecución de metas son el principal motor. Prioriza la eficacia por encima de cualquier otro factor. Muestra alta ambición y competitividad.", fit: "Ajuste Alto en culturas agresivas, roles de ventas, desarrollo de negocio o áreas con alta presión por objetivos." },
        Alto: { behavior: "Valora mucho alcanzar metas, pero con una moderación que permite considerar el proceso o el impacto en el equipo. Es un perfil altamente productivo.", fit: "Buen Ajuste en la mayoría de los roles que requieren responsabilidad y enfoque en la entrega." },
        Moderado: { behavior: "El logro es importante, pero no define su rol. Puede ser un buen ejecutor de tareas definidas, pero no un driver constante de metas. Necesita dirección clara.", fit: "Ajuste Funcional en roles operativos o de soporte donde el proceso es más importante que la superación de métricas." },
        Recesivo: { behavior: "El rendimiento cuantitativo tiene baja prioridad. El candidato se desmotiva por la presión de métricas y prefiere enfocarse en la calidad, el aprendizaje o la colaboración.", fit: "Bajo Ajuste en culturas de alto rendimiento o roles con compensación variable basada en objetivos." }
    },
    B: {
        Dominante: { behavior: "La cohesión y el soporte al grupo son la prioridad absoluta. Siempre actúa como facilitador, buscando la armonía y asegurando que todos se sientan incluidos.", fit: "Ajuste Alto en roles de liderazgo, people management, gestión de proyectos transversales o en culturas con fuerte enfoque en el bienestar." },
        Alto: { behavior: "Valora la dinámica grupal y contribuye activamente al éxito colectivo. Busca la interacción y prefiere trabajar en ambientes de consenso.", fit: "Buen Ajuste en la mayoría de los entornos de equipo. Es un compañero confiable y solidario." },
        Moderado: { behavior: "Funciona bien en equipo, pero puede aislarse para trabajar. La colaboración es una herramienta, no un valor fundamental. No invierte esfuerzo extra en la cohesión.", fit: "Ajuste Funcional en equipos equilibrados. Necesita que se le recuerde la importancia de compartir información." },
        Recesivo: { behavior: "Prefiere la autonomía e independencia. La interacción constante es vista como una distracción, y puede resistirse a invertir tiempo en la dinámica de grupo.", fit: "Bajo Ajuste en roles altamente colaborativos. Mejor en puestos de especialista individual o donde se requiera gran concentración solitaria." }
    },
    C: {
        Dominante: { behavior: "Es un Agente de Transformación. Su mayor motivación es cuestionar el status quo y experimentar. Acepta el riesgo y el fallo como parte del proceso de mejora.", fit: "Ajuste Alto en I+D, Estrategia, Innovación, Agile Teams o culturas de rápida evolución (Startups, Tech)." },
        Alto: { behavior: "Está abierto al cambio y busca formas activas de mejorar su trabajo y el de la empresa. Ofrece ideas con frecuencia, aunque sabe cuándo moderar.", fit: "Buen Ajuste en culturas que esperan proactividad y pensamiento lateral." },
        Moderado: { behavior: "No es un generador de ideas, pero está dispuesto a implementar las ideas de otros. Prefiere que los métodos innovadores estén probados antes de adoptarlos.", fit: "Ajuste Funcional en áreas donde se valora la ejecución estable de nuevos procesos." },
        Recesivo: { behavior: "Evita el cambio y la incertidumbre que trae la innovación. Se siente más seguro con métodos establecidos y puede resistirse activamente a los nuevos procesos.", fit: "Bajo Ajuste en empresas en reestructuración o que exigen la adaptación constante a nuevas herramientas o mercados." }
    },
    D: {
        Dominante: { behavior: "Necesita Estructura y Previsibilidad. Se adhiere estrictamente a protocolos, valora la seguridad laboral y se siente incómodo con la ambigüedad o el caos.", fit: "Ajuste Alto en roles de cumplimiento (Compliance), Operaciones, Finanzas, o en organizaciones con burocracia necesaria." },
        Alto: { behavior: "Valora los procesos claros y es un perfil metódico y ordenado. Contribuye a la estandarización y reduce errores por falta de método.", fit: "Buen Ajuste en la mayoría de los roles que requieren organización y atención al detalle." },
        Moderado: { behavior: "Entiende la necesidad de procesos, pero no se siente limitado por su ausencia. Es capaz de crear su propia estructura o trabajar con cierta ambigüedad.", fit: "Ajuste Funcional en roles intermedios que requieren tanto orden como flexibilidad." },
        Recesivo: { behavior: "Prioriza la flexibilidad y la velocidad sobre la estructura. Ve los procesos detallados como un obstáculo y prefiere un entorno más orgánico y dinámico.", fit: "Bajo Ajuste en roles donde el incumplimiento de protocolos estrictos podría tener consecuencias graves (ej. seguridad, medicina, control de calidad)." }
    },
    E: {
        Dominante: { behavior: "La superación y el crecimiento personal son la mayor recompensa. Invierte tiempo y esfuerzo en dominar nuevas habilidades y busca activamente desafíos de aprendizaje.", fit: "Ajuste Alto en entornos de alta especialización (ej. R&D, Tech), o en programas de trainee y management que ofrecen formación continua." },
        Alto: { behavior: "Tiene una mentalidad de crecimiento y valora que la empresa invierta en su formación. Muestra alta curiosidad y deseo de ser un experto en su campo.", fit: "Buen Ajuste en la mayoría de los roles profesionales. Perfil que se auto-motiva a mejorar." },
        Moderado: { behavior: "Aprende lo que necesita para su rol actual y lo aplica, pero no busca activamente nuevas áreas de conocimiento. Ve la formación como una tarea, no como una pasión.", fit: "Ajuste Funcional en roles estables donde la tecnología o el mercado cambian lentamente." },
        Recesivo: { behavior: "No se motiva por el aprendizaje en sí mismo. Prefiere hacer lo que ya domina y puede resistirse a asumir tareas que requieran una curva de aprendizaje significativa.", fit: "Bajo Ajuste en entornos de cambio rápido o en roles donde el conocimiento técnico debe ser actualizado constantemente." }
    }
};

const getInterpretation = (score: number): IInterpretation => {
    if (score >= 40) return { level: 'Dominante', color: 'success' };
    if (score >= 30) return { level: 'Alto', color: 'info' };
    if (score >= 20) return { level: 'Moderado', color: 'warning' };
    return { level: 'Recesivo', color: 'error' };
};

function CircularProgressWithLabel(props: CircularProgressProps & { value: number, score: number }) {
    return (
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress variant="determinate" {...props} size={100} thickness={4} />
            <Box
                sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Typography variant="h5" component="div" color="text.primary" fontWeight="600">
                    {`${props.score}`}
                </Typography>
            </Box>
        </Box>
    );
}

const SurveyValuesProfile = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { survey, responses: userResponses } = (location.state as LocationState) || {};
    console.log("SUUU ", survey, userResponses)

    const formattedResponses = React.useMemo((): any => {
        if (!survey || !userResponses || !survey.json || !survey.json.pages) {
            return {};
        }

        const questionIdToNameMap = new Map<number, string>();
        survey.json.pages.forEach((page: any) => {
            if (page.elements) {
                page.elements.forEach((question: any) => {
                    if (question.id) {
                        questionIdToNameMap.set(question.id, question.name);
                    }
                });
            }
        });

        // const result: { [key: string]: string } = {};
        // userResponses.forEach(response => {
        //     const questionName = questionIdToNameMap.get(response.nativeQuestionId);
        //     if (questionName) {
        //         result[questionName] = response.content;
        //     }
        // });
        //
        // console.log("RESS", result)
        //
        // return result;
        return userResponses;
    }, [survey, userResponses]);

    const finalResults = React.useMemo(() => {
        const scores: Record<DimensionCode, number> = { A: 0, B: 0, C: 0, D: 0, E: 0 };

        for (const questionKey in formattedResponses) {
            const questionNumber = parseInt(questionKey.replace('Pregunta', ''), 10);
            const dimension = questionToDimensionMap[questionNumber];
            const rawScore = parseInt(formattedResponses[questionKey], 10);

            if (dimension && !isNaN(rawScore)) {
                const invertedScore = 6 - rawScore;
                scores[dimension] += invertedScore;
            }
        }

        return (Object.keys(scores) as DimensionCode[]).map(code => {
            const score = scores[code];
            const interpretation = getInterpretation(score);
            return {
                code,
                score,
                ...dimensionInfo[code],
                interpretation,
                texts: resultTexts[code][interpretation.level as InterpretationLevel],
            };
        }).sort((a, b) => a.code.localeCompare(b.code));

    }, [formattedResponses]);

    const generatePdfReport = () => {
        const doc = new jsPDF('p', 'mm', 'a4'); // 'p' for portrait, 'mm' for units
        const margin = 15;
        const pageWidth = doc.internal.pageSize.getWidth();
        const textWidth = pageWidth - 2 * margin;
        let y = margin;

        // --- Auxiliary function to handle text splitting and page breaks ---
        const addText = (text: string, newY: number, size: number, style: 'normal' | 'bold', x: number = margin, maxWidth: number = textWidth, color: string = '#000000') => {
            doc.setFont('helvetica', style);
            doc.setFontSize(size);
            doc.setTextColor(color);

            // Split text to fit within maxWidth
            const splitText = doc.splitTextToSize(text, maxWidth);

            let currentY = newY;
            splitText.forEach((line: string) => {
                if (currentY > doc.internal.pageSize.height - margin) {
                    doc.addPage();
                    currentY = margin;
                }
                doc.text(line, x, currentY);
                currentY += size * 0.35; // Increment line height based on font size (approximate)
            });

            return currentY; // Return final Y position
        };

        // --- HEADER ---
        y = addText('moodbit', 10, 8, 'normal', margin); // Fictional Logo/Header Text
        doc.setDrawColor('#cccccc');
        doc.line(margin, y + 1, pageWidth - margin, y + 1); // Horizontal line under the header
        y += 8;

        y = addText('Reporte Detallado del Perfil de Valores Culturales', y, 18, 'bold');
        y += 10;

        // --- SECTION 1: Resultados y Marco ---
        y = addText('1. Resultados del Candidato y Marco Interpretativo', y, 14, 'bold');
        y += 5;

        // Sub-Section A: Resultados del Candidato
        y = addText('A. Resultados del Candidato (Puntuación Bruta 10-50)', y, 12, 'bold');
        y += 3;

        const candidateData = finalResults.map(r => [
            `${r.name}`,
            `${r.score}`,
            r.interpretation.level,
            r.texts.behavior.split('. ')[0].split('.').join('.') // Use first sentence
        ]);

        autoTable(doc, {
            startY: y,
            head: [['Dimensión', 'Puntuación', 'Rango de Prioridad', 'Comportamiento Intrínseco']],
            body: candidateData,
            margin: { left: margin, right: margin },
            styles: { fontSize: 9 },
            headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold' },
            columnStyles: {
                1: { cellWidth: 20, halign: 'center' },
                2: { cellWidth: 25, halign: 'center' },
            }
        });
        y = (doc as any).lastAutoTable.finalY + 8;

        // Sub-Section B: Marco de Interpretación
        y = addText('B. Marco de Interpretación de Rangos', y, 12, 'bold');
        y += 3;

        autoTable(doc, {
            startY: y,
            head: [['Rango de Puntaje Bruto (10-50)', 'Interpretación del Rango', 'Definición del Componente (Valor)', 'Implicación en el Ajuste Cultural']],
            body: [
                ['Dominante (40-50)', 'Prioridad Máxima. Motivación central y definitoria.', 'Valor que guía activamente la toma de decisiones y el esfuerzo diario.', 'Ajuste Crítico: Si el valor coincide, el match es máximo. Si no, hay fricción constante.'],
                ['Alto (30-39)', 'Prioridad Fuerte. Preferencia clara y activa.', 'Valor buscado y manifestado con frecuencia en el entorno laboral.', 'Buen Ajuste: Perfil altamente compatible con culturas que promueven este valor.'],
                ['Moderado (20-29)', 'Prioridad Media/Equilibrada. Motivación secundaria.', 'El valor se activa según el contexto, pero no es una fuente primaria de impulso.', 'Ajuste Funcional: Perfil adaptable, que no es fuente de motivación ni de conflicto.'],
                ['Recesivo (10-19)', 'Prioridad Mínima. Tendencia a evitar el valor.', 'El valor es secundario e incluso evitado por el candidato.', 'Desajuste Potencial: Alto riesgo de desmotivación o rotación si la cultura lo exige fuertemente.'],
            ],
            margin: { left: margin, right: margin },
            styles: { fontSize: 9, cellPadding: 2 },
            headStyles: { fillColor: [150, 150, 150], textColor: 255, fontStyle: 'bold' },
            columnStyles: {
                0: { cellWidth: 35 },
                1: { cellWidth: 35 },
                2: { cellWidth: 45 },
                3: { cellWidth: 50 },
            }
        });
        y = (doc as any).lastAutoTable.finalY + 12;

        // --- SECTION 2: Análisis Detallado del Perfil Intrínseco ---
        y = addText('2. Análisis Detallado del Perfil Intrínseco', y, 14, 'bold');
        y += 5;

        // Introductory text
        y = addText('El candidato exhibe un perfil marcadamente Dinámico y Orientado al Avance. Sus motivaciones principales se centran en el logro personal y el cambio.', y, 10, 'normal');
        y += 5;

        const dominant = finalResults.filter(r => r.interpretation.level === 'Dominante');
        const high = finalResults.filter(r => r.interpretation.level === 'Alto');
        const moderate = finalResults.filter(r => r.interpretation.level === 'Moderado');
        const recessive = finalResults.filter(r => r.interpretation.level === 'Recesivo');

        const addAnalysisPoint = (title: string, results: typeof finalResults, description: string) => {
            if (results.length > 0) {
                y = addText(`• ${title}`, y, 10, 'bold', margin, textWidth); // Bullet point with title
                const valuesText = results.map(r => `${r.name} (${r.score})`).join(' y ');
                const fullText = description.replace('{values}', valuesText);
                y = addText(fullText, y, 10, 'normal', margin + 5, textWidth - 5); // Indented text
                y += 2;
            }
        };

        addAnalysisPoint('Valores Propulsores (Dominantes):', dominant, `El candidato se define por ser un Agente de Cambio ({values}). Su energía se destina a la ejecución eficiente y a la búsqueda constante de nuevas y mejores formas de operar.`);
        addAnalysisPoint('Valores de Soporte (Alto):', high, `El fuerte deseo de {values} apoya sus valores Dominantes, asegurando que su búsqueda de resultados e innovación se base en el expertise y la mejora de habilidades.`);
        addAnalysisPoint('Valores Funcionales (Moderado):', moderate, `La Colaboración ({values}) es práctica; trabajará bien en equipo, pero el consenso o la armonía no son su fin último. El equipo es un medio para lograr sus resultados (Valor Dominante).`);
        addAnalysisPoint('Valores de Fricción (Recesivo):', recessive, `El valor menos prioritario para el candidato es la {values}. Esto indica una baja tolerancia a la rutina, la burocracia y los procesos rígidos.`);

        y += 5;

        // --- SECTION 3: Análisis del Ajuste Cultural (Hardcoded Example from PDF) ---
        // Note: Since the component doesn't have the company/role values, this is a fixed example.
        y = addText('3. Análisis del Ajuste Cultural (Analista Administrativo)', y, 14, 'bold');
        y += 5;
        y = addText('El Ajuste Cultural se evalúa en el contexto de su empresa: una organización estable con baja rotación en el área administrativa y alta valoración de la cohesión del equipo.', y, 10, 'normal');
        y += 5;

        // --- Critical Mismatch Table ---
        y = addText('A. Desajuste Crítico: Estabilidad y Certeza', y, 12, 'bold');
        y += 3;

        autoTable(doc, {
            startY: y,
            head: [['Valor de la Empresa', 'Valor del Candidato', 'Conclusión de Ajuste']],
            body: [
                ['Estabilidad de Procesos y Baja Rotación (Alta)', `Estabilidad/Certeza: Recesivo (${recessive[0]?.score || 18})`, 'Bajo Ajuste (Riesgo de Rotación): Este es el punto de mayor fricción. El candidato se sentirá restringido y desmotivado por la naturaleza rutinaria y los procesos bien establecidos de un entorno administrativo estable. Su motivación intrínseca lo empuja a la flexibilidad y al cambio, opuesto a lo que la empresa valora.'],
                ['Apego a Normas/Procedimientos (Alto)', `Innovación/Creatividad Dominante (${dominant.find(r => r.code === 'C')?.score || 43})`, 'Riesgo de Conflicto: El impulso dominante por Innovar lo llevará a cuestionar o intentar cambiar constantemente los procesos establecidos. Si el rol requiere estricto cumplimiento normativo, esto puede generar conflictos operativos.'],
            ],
            margin: { left: margin, right: margin },
            styles: { fontSize: 9, minCellHeight: 20 },
            headStyles: { fillColor: [255, 100, 100], textColor: 0, fontStyle: 'bold' },
        });
        y = (doc as any).lastAutoTable.finalY + 8;

        // --- Teamwork and Results Table ---
        y = addText('B. Ajuste al Trabajo en Equipo y Resultados', y, 12, 'bold');
        y += 3;

        autoTable(doc, {
            startY: y,
            head: [['Valor de la Empresa', 'Valor del Candidato', 'Conclusión de Ajuste']],
            body: [
                ['Cohesión y Unidad de Equipo (Alta)', `Colaboración/Equipo: Moderado (${moderate.find(r => r.code === 'B')?.score || 26})`, 'Ajuste Funcional: El candidato cumplirá con las expectativas de trabajo en equipo, pero no será la persona que impulse activamente la unidad o el bienestar del grupo. Su foco estará en la eficacia individual (Resultado 44), lo cual puede ser percibido como falta de "unión" si el equipo prioriza la armonía.'],
                ['Logro y Profesionalismo (Medio/Alto)', `Resultado (${dominant.find(r => r.code === 'A')?.score || 44}) y Desarrollo (${high.find(r => r.code === 'E')?.score || 38})`, 'Ajuste Máximo: El candidato es un activo excepcional para lograr metas ambiciosas y mejorar sus competencias. Su desempeño individual será proactivo y de alta calidad.'],
            ],
            margin: { left: margin, right: margin },
            styles: { fontSize: 9, minCellHeight: 20 },
            headStyles: { fillColor: [100, 100, 255], textColor: 255, fontStyle: 'bold' },
        });
        y = (doc as any).lastAutoTable.finalY + 12;


        // --- SECTION 4: Conclusión y Recomendación ---
        y = addText('4. Conclusión y Recomendación', y, 14, 'bold');
        y += 5;

        // Conclusion
        y = addText('Conclusión', y, 12, 'bold');
        y += 3;
        const conclusionText = `El candidato es un perfil de Alto Potencial y Agente de Cambio. Sus motivaciones (Resultado, Innovación y Desarrollo) son extremadamente altas. Sin embargo, su valor Recesivo en Estabilidad/Certeza (${recessive[0]?.score || 18}) genera un desajuste cultural significativo con la naturaleza predecible y la baja rotación del área administrativa.`;
        y = addText(conclusionText, y, 10, 'normal', margin, textWidth);
        y += 8;

        // Recommendation
        y = addText('Recomendación Estratégica', y, 12, 'bold');
        y += 3;

        y = addText('Contratar si:', y, 10, 'bold', margin, textWidth);
        y += 1;
        y = addText(`• El rol de Analista Administrativo tiene un mandato claro de Optimización y Transformación de Procesos.`, y, 10, 'normal', margin + 5, textWidth - 5);
        y += 1;
        y = addText(`• Se le puede garantizar un flujo constante de proyectos y desafíos nuevos para satisfacer su necesidad de Desarrollo y evitar que se aburra con la rutina.`, y, 10, 'normal', margin + 5, textWidth - 5);
        y += 5;

        y = addText('No Contratar si:', y, 10, 'bold', margin, textWidth);
        y += 1;
        y = addText(`• El rol se limita a la ejecución estricta de procedimientos ya existentes o a la gestión rutinaria y monótona.`, y, 10, 'normal', margin + 5, textWidth - 5);
        y += 1;
        y = addText(`• En este escenario, el riesgo de desmotivación, fricción con la cultura estable y rotación temprana es alto.`, y, 10, 'normal', margin + 5, textWidth - 5);

        doc.save('Reporte_Perfil_Valores.pdf');
    };

    if (!survey) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
                <Typography variant="h6">Gracias por completar la encuesta.</Typography>
                {/*<Button variant="contained" onClick={() => navigate(-1)} sx={{ mt: 2 }}>*/}
                {/*    Volver*/}
                {/*</Button>*/}
            </Box>
        );
    }

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', p: 2, mx: { xs: 1, md: 3 }, mt: 2 }}>
                <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }} aria-label="go back">
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h5" component="h1" fontWeight="600" sx={{ flexGrow: 1 }}>
                    {survey.title} - Perfil de Valores
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<DownloadIcon />}
                    onClick={generatePdfReport}
                    sx={{ ml: 2 }}
                >
                    Descargar Reporte
                </Button>
            </Box>

            <Box sx={{ flexGrow: 1, px: { xs: 2, md: 4 }, py: 2 }}>
                <Grid container spacing={3}>
                    {finalResults.map((result) => (
                        <Grid xs={12} md={6} lg={4} key={result.code}>
                            <Card variant="outlined" sx={{ borderRadius: 4, height: '100%', display: 'flex', flexDirection: 'column', p: 1 }}>
                                <CardContent sx={{ textAlign: 'center', flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', p: 3 }}>
                                    <div>
                                        <Typography variant="h6" component="h2" fontWeight="600" gutterBottom>
                                            {result.name}
                                        </Typography>
                                        <Box sx={{ my: 3 }}>
                                            <CircularProgressWithLabel
                                                value={(result.score / 50) * 100}
                                                score={result.score}
                                                color={result.color}
                                            />
                                        </Box>
                                    </div>
                                    <Box sx={{ textAlign: 'left' }}>
                                        <Chip
                                            label={result.interpretation.level}
                                            color={result.interpretation.color}
                                            sx={{ mb: 2, fontWeight: 'bold', width: '100%' }}
                                        />
                                        <Typography variant="body2" color="text.secondary" paragraph>
                                            {result.texts.behavior}
                                        </Typography>
                                        <Divider sx={{ my: 1.5 }} />
                                        <Typography variant="caption" color="text.secondary" component="div" fontWeight="bold" sx={{ mb: 0.5 }}>
                                            AJUSTE CULTURAL
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {result.texts.fit}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </>
    );
};

export default SurveyValuesProfile;